import React from "react";
import { TAPSOA_LOGO_PNG } from "../../config/brandAssets";

/**
 * Suspense fallback: TAPSOA wordmark, soft brand backdrop, and a11y-friendly status.
 * - fullscreen: first paint before layout chrome (e.g. lazy PublicLayout / dashboard shell)
 * - embedded: inside an existing layout main / dashboard content area
 */
export function BrandedRouteFallback({ variant = "embedded" }) {
  const isFullscreen = variant === "fullscreen";

  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      className={
        isFullscreen
          ? "relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden bg-tapsoa-gray-light px-6 py-16"
          : "relative flex w-full flex-1 flex-col items-center justify-center px-4 py-12 md:py-20 min-h-[min(70vh,28rem)]"
      }
    >
      {isFullscreen && (
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute -left-24 top-0 h-72 w-72 rounded-full bg-tapsoa-red/[0.08] blur-3xl" />
          <div className="absolute -right-16 bottom-0 h-64 w-80 rounded-full bg-tapsoa-green/[0.07] blur-3xl" />
          <div
            className="absolute inset-0 opacity-[0.35]"
            style={{
              backgroundImage:
                "radial-gradient(circle at center, rgba(153,27,27,0.06) 1px, transparent 1px)",
              backgroundSize: "20px 20px",
            }}
          />
        </div>
      )}

      <div
        className={[
          "relative flex flex-col items-center gap-8 rounded-2xl border border-tapsoa-black/[0.06] bg-tapsoa-white/90 px-10 py-10 text-center shadow-lg shadow-tapsoa-black/[0.04] backdrop-blur-sm",
          isFullscreen ? "max-w-sm" : "max-w-md",
        ].join(" ")}
      >
        <div className="flex flex-wrap items-center justify-center gap-3">
          <img
            src={TAPSOA_LOGO_PNG}
            alt="TAPSOA"
            width={200}
            height={56}
            className="h-14 w-auto max-w-[min(240px,85vw)] object-contain object-center"
            decoding="async"
          />
        </div>

        <div className="flex flex-col items-center gap-4">
          <div
            className="h-9 w-9 rounded-full border-2 border-tapsoa-red/20 border-t-tapsoa-red motion-safe:animate-spin motion-reduce:animate-none"
            aria-hidden="true"
          />
          <p className="text-sm font-medium text-tapsoa-gray-medium">Loading…</p>
        </div>
      </div>

      <span className="sr-only">Loading the application</span>
    </div>
  );
}

export default BrandedRouteFallback;
